import "./sentry";
import ReactDOM from "react-dom/client";

import { UnauthorizedException } from "@eisox/api";
import { AuthProvider, useAuthContext, type AuthState } from "@eisox/auth";
import { HybridTooltip, LocaleProvider, Toaster } from "@eisox/design-system";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";

import { authApi } from "~/api";
import { Error } from "~/components/error";
import { NotFound } from "~/components/not-found";
import { MutationFeedback, mutationCache } from "~/integrations/tanstack-query";
import { authTokenStorage } from "~/utils/auth-token.utils";
import { initLanguageFromParaglide } from "~/utils/language.utils";

import { getLocale } from "./paraglide/runtime";
import { routeTree } from "./routeTree.gen";

import "./styles/styles.css";

// oxlint-disable-next-line turbo/no-undeclared-env-vars -- Vite built-in, not a turbo input
if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
  const { network } = await import("virtual:msw");
  network.configure({ handlers: [], onUnhandledFrame: "bypass" });
  await network.enable();
}

void initLanguageFromParaglide();

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: (failureCount, error) => !(error instanceof UnauthorizedException) && failureCount < 3,
    },
  },
});
const router = createRouter({
  routeTree,
  context: {
    // auth will be passed down from App component
    auth: undefined as unknown as AuthState,
    queryClient,
  },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: Error,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuthContext("InnerApp");
  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider
        authApi={authApi}
        storage={authTokenStorage}
        onSignedOut={() => router.navigate({ to: "/sign-in", search: { redirect: "/" } })}
      >
        <LocaleProvider locale={getLocale()}>
          <Toaster />
          <MutationFeedback />
          <HybridTooltip.TouchProvider>
            <InnerApp />
          </HybridTooltip.TouchProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById("app");

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
